import { Component, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Password } from 'primeng/password';
import { Textarea } from 'primeng/textarea';
import { Select } from 'primeng/select';
import { Checkbox } from 'primeng/checkbox';
import { Tooltip } from 'primeng/tooltip';
import { Fluid } from 'primeng/fluid';
import { CompanyService } from '@/app/services/company.service';
import { CountryService } from '@/app/services/country.service';
import { Company, CompanyStripeConfig } from '@/lib-backend';

@Component({
    selector: 'app-company-edit-dialog',
    standalone: true,
    imports: [FormsModule, Button, Dialog, InputText, Password, Textarea, Select, Checkbox, Tooltip, Fluid],
    templateUrl: './company-edit-dialog.html',
})
export class CompanyEditDialog {
    private readonly companyService = inject(CompanyService);
    protected readonly countryService = inject(CountryService);

    readonly visible = model.required<boolean>();
    /** null → create mode; otherwise edit. */
    readonly company = input<Company | null>(null);
    readonly created = output<Company>();

    protected readonly mode = computed<'create' | 'edit'>(() =>
        this.company() ? 'edit' : 'create'
    );
    protected readonly title = computed(() =>
        this.mode() === 'create' ? 'Add company' : `Edit — ${this.company()!.Name}`
    );

    protected readonly name = signal('');
    protected readonly countryCode = signal<string | null>(null);
    protected readonly address = signal('');
    protected readonly regDetails = signal('');
    protected readonly liveChatGroupId = signal('');

    protected readonly stripeIsEnabled = signal(false);
    protected readonly stripePublishableKey = signal('');
    protected readonly stripeSecretKey = signal('');
    protected readonly stripeWebhookSecret = signal('');

    protected readonly nameError = signal<string | null>(null);
    protected readonly countryError = signal<string | null>(null);
    protected readonly addressError = signal<string | null>(null);
    protected readonly regError = signal<string | null>(null);
    protected readonly liveChatGroupIdError = signal<string | null>(null);
    protected readonly stripePublishableKeyError = signal<string | null>(null);
    protected readonly stripeSecretKeyError = signal<string | null>(null);
    protected readonly stripeWebhookSecretError = signal<string | null>(null);
    protected readonly generalError = signal<string | null>(null);

    protected readonly saving = signal(false);

    protected readonly webhookUrl = `${window.location.origin}/api/payment/stripeWebhook`;
    protected readonly webhookCopied = signal(false);

    protected readonly countryOptions: { label: string; value: string }[] =
        this.countryService.getAll().map(c => ({ label: `${c.name} (${c.code})`, value: c.code }));

    constructor() {
        // Sync form with input company whenever the dialog opens.
        effect(() => {
            if (!this.visible()) return;
            const c = this.company();
            this.name.set(c?.Name ?? '');
            this.countryCode.set(c?.CountryCode ?? null);
            this.address.set(c?.Address ?? '');
            this.regDetails.set(c?.RegistrationDetails ?? '');
            this.liveChatGroupId.set(c?.LiveChatConfig.GroupId ?? '');
            this.stripeIsEnabled.set(c?.Stripe.IsEnabled ?? false);
            this.stripePublishableKey.set(c?.Stripe.PublishableKey ?? '');
            this.stripeSecretKey.set(c?.Stripe.SecretKey ?? '');
            this.stripeWebhookSecret.set(c?.Stripe.WebhookSecret ?? '');
            this.clearErrors();
        });
    }

    private clearErrors(): void {
        this.nameError.set(null);
        this.countryError.set(null);
        this.addressError.set(null);
        this.regError.set(null);
        this.liveChatGroupIdError.set(null);
        this.stripePublishableKeyError.set(null);
        this.stripeSecretKeyError.set(null);
        this.stripeWebhookSecretError.set(null);
        this.generalError.set(null);
    }

    protected close(): void {
        this.visible.set(false);
    }

    protected async copyWebhookUrl(): Promise<void> {
        try {
            await navigator.clipboard.writeText(this.webhookUrl);
            this.webhookCopied.set(true);
            setTimeout(() => this.webhookCopied.set(false), 1500);
        } catch {
            // Clipboard write can fail in non-secure contexts; silent.
        }
    }

    protected async save(): Promise<void> {
        this.clearErrors();

        const name = this.name().trim();
        const country = this.countryCode();
        const address = this.address().trim();
        const reg = this.regDetails().trim();
        const groupInput = this.liveChatGroupId().trim();
        const groupId: string | null = groupInput.length > 0 ? groupInput : null;

        const stripeEnabled = this.stripeIsEnabled();
        const stripePub = this.stripePublishableKey().trim();
        const stripeSec = this.stripeSecretKey().trim();
        const stripeHook = this.stripeWebhookSecret().trim();

        let invalid = false;
        if (!name) { this.nameError.set('Name is required'); invalid = true; }
        if (!country) { this.countryError.set('Country is required'); invalid = true; }
        if (!address) { this.addressError.set('Address is required'); invalid = true; }
        if (!reg) { this.regError.set('Registration details are required'); invalid = true; }
        if (groupId !== null && !/^\d+$/.test(groupId)) {
            this.liveChatGroupIdError.set('Group ID must be a number');
            invalid = true;
        }
        if (stripeEnabled) {
            if (!stripePub) { this.stripePublishableKeyError.set('Required when Stripe is enabled'); invalid = true; }
            if (!stripeSec) { this.stripeSecretKeyError.set('Required when Stripe is enabled'); invalid = true; }
            if (!stripeHook) { this.stripeWebhookSecretError.set('Required when Stripe is enabled'); invalid = true; }
        }
        if (invalid) return;

        const stripe: CompanyStripeConfig = {
            IsEnabled: stripeEnabled,
            PublishableKey: stripePub,
            SecretKey: stripeSec,
            WebhookSecret: stripeHook,
        };

        this.saving.set(true);
        try {
            const editing = this.company();
            const liveChatChanged = editing && groupId !== (editing.LiveChatConfig.GroupId ?? null);
            const stripeChanged = editing && (
                stripe.IsEnabled !== editing.Stripe.IsEnabled ||
                stripe.PublishableKey !== editing.Stripe.PublishableKey ||
                stripe.SecretKey !== editing.Stripe.SecretKey ||
                stripe.WebhookSecret !== editing.Stripe.WebhookSecret
            );
            const outcome = editing
                ? await this.companyService.updateCompany({
                    CompanyId: editing.Id,
                    Name: name !== editing.Name ? name : null,
                    Address: address !== editing.Address ? address : null,
                    RegistrationDetails: reg !== editing.RegistrationDetails ? reg : null,
                    CountryCode: country !== editing.CountryCode ? country : null,
                    Stripe: stripeChanged ? stripe : null,
                    LiveChatConfig: liveChatChanged ? { GroupId: groupId } : null,
                })
                : await this.companyService.createCompany({
                    Name: name,
                    Address: address,
                    RegistrationDetails: reg,
                    Country: country!,
                    Stripe: stripe,
                    LiveChatConfig: { GroupId: groupId },
                });

            if (outcome.result === 'Ok') {
                if (!editing) this.created.emit(outcome.company);
                this.close();
                return;
            }
            switch (outcome.result) {
                case 'NameAlreadyUsed':
                    this.nameError.set('A company with this name already exists');
                    break;
                case 'NotFound':
                    this.generalError.set('Company no longer exists');
                    break;
                case 'InvalidActivePaymentMethod':
                    this.generalError.set('Cannot disable Stripe — at least one website owned by this company has Stripe selected as its active payment method.');
                    break;
                default:
                    this.generalError.set('Failed to save company');
            }
        } finally {
            this.saving.set(false);
        }
    }
}
